import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daemab3ep053ijub1e2g";

type Status = "idle" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // honeypot: silent success, do not submit
    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt") payload.append(key, String(value));
    });

    setStatus("idle");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const okCode = parsed?.code === "OK";

      if (res.ok && okCode) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "订阅失败，请稍后再试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络似乎开小差了，请稍后重试。");
      setStatus("error");
    }
  }

  return (
    <section id="contact" className="bg-secondary-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-15">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20amber%20and%20terracotta%20flowing%20pattern%20background%20with%20soft%20organic%20curves%2C%20editorial%20minimal%20style%2C%20subtle%20texture%2C%20no%20text%2C%20no%20people&width=1600&height=560&seq=cta-bg-1&orientation=landscape"
          alt=""
          className="h-full w-full object-cover object-center"
        />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-10">
          <div>
            <h2 className="font-heading text-2xl md:text-4xl font-black leading-tight text-background-50">
              每周风向，直送邮箱
            </h2>
            <p className="mt-3 max-w-lg text-sm md:text-base leading-relaxed text-background-200/85">
              订阅「九一吃瓜每周风向」，每周一篇热点观点梳理、一份争论焦点清单，
              一次帮你理清本周大家都怎么看。不刷屏，可随时退订。
            </p>
            <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-background-200/80">
              <li className="flex items-center gap-1.5">
                <i className="ri-mail-check-line text-secondary-300" aria-hidden="true"></i>每周一篇精选
              </li>
              <li className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-secondary-300" aria-hidden="true"></i>重视隐私
              </li>
              <li className="flex items-center gap-1.5">
                <i className="ri-loop-left-line text-secondary-300" aria-hidden="true"></i>一键退订
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-background-200/20 bg-background-50/10 p-5 backdrop-blur md:p-7">
            <form id="weekly-subscribe" data-readdy-form onSubmit={handleSubmit} noValidate>
              <label htmlFor="subscribe-email" className="text-sm font-medium text-background-100">
                你的常用邮箱
              </label>
              <div className="mt-2.5 flex flex-col gap-3 sm:flex-row">
                <input
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@example.com"
                  className="w-full rounded-md border border-background-200/40 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-400/30"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-400 cursor-pointer"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                  立即订阅
                </button>
              </div>
              <input
                type="text"
                name="company_alt"
                className="hp-guard"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" && (
                <p className="mt-3 flex items-start gap-2 rounded-lg bg-accent-500/20 px-3 py-2.5 text-sm text-background-100" role="status">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-base" aria-hidden="true"></i>
                  订阅成功！请查收邮箱完成确认，我们下周见。
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 flex items-start gap-2 rounded-lg bg-primary-500/25 px-3 py-2.5 text-sm text-background-100" role="alert">
                  <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" aria-hidden="true"></i>
                  {formError || "订阅失败，请稍后再试。"}
                </p>
              )}
            </form>
            <p className="mt-3 text-xs text-background-200/70">
              提交即表示你同意接收我们的周报，我们不会向任何第三方共享你的邮箱。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}